/**
 * The plan where money is managed — the shared reads and words of Billing's Plan section, the pay page, the card
 * replacement and the shell's plan banner (plan-020 **WAPP-BILL**; design §32.6.6, §32.6.7, §32.10, §32.12.3 Q-23…Q-28,
 * §32.17; R-APP-1, R-APP-4, ED-22).
 *
 * ONE READ. Every surface reads the organization's SHARED balance's subscription through the same query keys as
 * Finance → Plans (`["billing-accounts", account]`, `["balance-subscription", balance]`), so a payment or a card change
 * made on one refreshes all of them. Plans are bought for the shared balance only (OD-7): a dedicated balance never
 * holds one. Reading needs `account.finance.read`; paying, replacing the card and cancelling need
 * `account.finance.manage` (Owner, Admin, Finance — ADR 0033), which the API checks again.
 *
 * NO FIGURE IS TYPED HERE: every amount, period and count is the API's (`no-literal-plan-figure`,
 * `no-literal-talk-price`).
 */
import { useMutation, useQuery } from "@tanstack/react-query";

import { useGateway } from "../../api/provider.js";
import type { OperationPaymentView } from "../../api/slice/billing-operation.js";
import type { BalanceSubscriptionView } from "../../api/slice/subscription.js";
import { portalText } from "../../i18n/portal.js";
import { roleHolds, useOpenAccount } from "../account-admin/overview/account-overview-screen.js";
import { longDateText } from "../finance/plans/plan-copy.js";
import { heldNameOf } from "../finance/plans/held-plan.js";
import { goToPaymentPage } from "./payment-page.js";

/** Billing's search: the pay page (`?pay=<operation>`, design §32.10) and the card replacement (`?card=replace`). */
export interface BillingSearch {
  readonly pay?: string;
  readonly card?: "replace";
}

const OPERATION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Reads Billing's search: an operation id (and nothing else) for `pay`, the one word `replace` for `card`. */
export function billingSearchOf(raw: Readonly<Record<string, unknown>>): BillingSearch {
  const pay = raw["pay"];
  const card = raw["card"];
  return {
    ...(typeof pay === "string" && OPERATION_ID.test(pay) ? { pay: pay.toLowerCase() } : {}),
    ...(card === "replace" ? { card: "replace" as const } : {}),
  };
}

/** A plan whose renewal is unpaid: its plan prices hold during the grace (`past_due`), then pay-as-you-go (`suspended`). */
export function renewalUnpaid(held: Pick<BalanceSubscriptionView, "state"> | null): boolean {
  return held !== null && (held.state === "past_due" || held.state === "suspended");
}

/** The shell banner's words for an unpaid renewal (Q-25, Q-26), or `null` while nothing is owed. Pure. */
export function planAlertCopyOf(held: BalanceSubscriptionView | null): { readonly tone: "caution" | "critical"; readonly title: string; readonly dek: string } | null {
  if (held === null) return null;
  const zone = held.billingTimezone;
  if (held.state === "past_due") {
    return {
      tone: "caution",
      title: portalText("billing", "alert.past_due_title", { plan: heldNameOf(held) }),
      dek:
        held.graceEndsAt === null
          ? portalText("subscription", "held.past_due", { grace_end: held.period === null ? "" : longDateText(held.period.end, zone) })
          : portalText("subscription", "held.past_due", { grace_end: longDateText(held.graceEndsAt, zone) }),
    };
  }
  if (held.state === "suspended") {
    return {
      tone: "critical",
      title: portalText("billing", "alert.suspended_title", { plan: heldNameOf(held) }),
      dek: portalText("subscription", "held.suspended", { period_end: held.period === null ? "" : longDateText(held.period.end, zone) }),
    };
  }
  return null;
}

/** The card replacement's amount sentence, by what the new card's first charge is (design §32.6.6). Pure. */
export function replacementAmountTextOf(establishment: "plan_charge" | "renewal_payment" | "credit_pack", total: string): string {
  return establishment === "renewal_payment" ? portalText("billing", "card.renewal", { total }) : portalText("billing", "card.credit_pack", { total });
}

/** What the person is asked to pay on the pay page, in a few words. */
export function payDekOf(payment: Pick<OperationPaymentView, "operationId" | "kind">, held: BalanceSubscriptionView | null): string {
  if (payment.kind !== "subscription_renewal" || held === null) return portalText("billing", "pay.dek_plain");
  if (held.payOperationId === payment.operationId && held.period !== null) {
    return portalText("billing", "pay.dek_renewal_period", {
      plan: heldNameOf(held),
      start: longDateText(held.period.start, held.billingTimezone),
      end: longDateText(held.period.end, held.billingTimezone),
    });
  }
  return portalText("billing", "pay.dek_renewal", { plan: heldNameOf(held) });
}

export interface SharedSubscription {
  readonly accountId: string | null;
  readonly canRead: boolean;
  readonly canManage: boolean;
  /** The shared balance's id, or `null` while unread or when the organization has none. */
  readonly billingAccountId: string | null;
  /** `undefined` while reading; `null` on pay as you go. */
  readonly held: BalanceSubscriptionView | null | undefined;
  readonly failed: boolean;
  readonly refetch: () => void;
}

/** The organization's shared balance and its running plan, for a finance reader (nothing is read for anyone else). */
export function useSharedSubscription(): SharedSubscription {
  const gateway = useGateway();
  const open = useOpenAccount();
  const accountId = open.accountId;
  const canRead = roleHolds(open.accountRole, "account.finance.read");
  const canManage = roleHolds(open.accountRole, "account.finance.manage");
  const enabled = accountId !== null && canRead;
  const balances = useQuery({ queryKey: ["billing-accounts", accountId ?? "none"], queryFn: () => gateway.listBillingAccounts(accountId ?? ""), enabled });
  const shared = balances.data?.find((one) => one.scope === "account") ?? null;
  const subscription = useQuery({
    queryKey: ["balance-subscription", shared?.billingAccountId ?? "none"],
    queryFn: () => gateway.readBalanceSubscription(shared?.billingAccountId ?? ""),
    enabled: enabled && shared !== null,
  });
  const held = !enabled ? null : balances.data !== undefined && shared === null ? null : subscription.data;
  return {
    accountId,
    canRead,
    canManage,
    billingAccountId: shared?.billingAccountId ?? null,
    held,
    failed: balances.isError || subscription.isError,
    refetch: () => {
      void balances.refetch();
      void subscription.refetch();
    },
  };
}

/**
 * "Pay now": asks the API for the processor's page (`POST /v1/billing-operations/{id}/pay`, a fresh 30-minute page each
 * time, design §32.10) and sends the browser there. When no page comes back (the payment is already being looked up, or
 * done), `onNoPage` gets the operation's state — the pay page shows it.
 */
export function usePayOperation(onNoPage: (view: OperationPaymentView) => void) {
  const gateway = useGateway();
  return useMutation({
    mutationFn: (operationId: string) => gateway.payOperation(operationId),
    onSuccess: (view) => {
      const url = view.nextActionUrl ?? view.payUrl;
      if (url !== null) {
        goToPaymentPage(url);
        return;
      }
      onNoPage(view);
    },
  });
}
